import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getUser } from "../network/api";
import { rankOf, ROLES } from "../constants/roles";
import { setSentryUser } from "../sentry";

// Single source of truth for the logged-in user. Replaces the 3+ duplicate
// getUser() fetches scattered across header/sider/pages.
const AuthContext = createContext({
  user: null,
  role: null,
  loading: true,
  refresh: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem("accessToken")) {
      setLoading(false);
      return;
    }
    try {
      const u = await getUser();
      setUser(u);
      localStorage.setItem("user", JSON.stringify(u));
    } catch {
      // leave cached user; interceptor handles 401/logout
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Attribute Sentry issues to the signed-in user. No-op when Sentry is disabled.
  useEffect(() => {
    setSentryUser(user);
  }, [user]);

  const role = user?.role || null;
  const value = {
    user,
    role,
    loading,
    refresh,
    rank: rankOf(role),
    isHardwareAdmin: rankOf(role) >= rankOf(ROLES.HARDWARE_ADMIN),
    isSupervisor: rankOf(role) >= rankOf(ROLES.SUPERVISOR),
    isSuperAdmin: role === ROLES.SUPER_ADMIN,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
